import React, { useEffect, useState } from 'react';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import { Package, DollarSign, Users, IndianRupee } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip
} from 'chart.js';
import { useSidebar } from '../../SidebarContext';
import Ripples from 'react-ripples'
import { decryptToken } from '../utils/getToken';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip);

const statusList = [
    { key: 'pending', label: 'Pending', color: '#f59e0b' },
    { key: 'processing', label: 'Processing', color: '#3b82f6' },
    { key: 'packed', label: 'Packed', color: '#10b981' },
    { key: 'shipped', label: 'Shipped', color: '#06b6d4' },
    { key: 'out for delivery', label: 'Out for Delivery', color: '#a855f7' },
    { key: 'delivered', label: 'Delivered', color: '#22c55e' },
    { key: 'cancelled', label: 'Cancelled', color: '#ef4444' },
];


// 📊 Stat Chart Card Component
const StatChartCard = ({ label, dataPoints, icon, iconColor = '#0f172a', bgColor = '#f1f5f9', comparison, timeRange, isCurrency = false, }) => {
    const total = dataPoints.reduce((a, b) => a + b, 0);
    const { percent, isPositive, currentSum, previousSum } = comparison;



    // const getChartLabels = (dataPoints, timeRange) => {
    //     const now = new Date();

    //     if (timeRange <= 30) {
    //         return [...Array(timeRange)].map((_, i) => {
    //             const date = new Date();
    //             date.setDate(now.getDate() - (timeRange - 1 - i));
    //             return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    //         });
    //     } else {
    //         const months = timeRange === 180 ? 6 : 12;
    //         return [...Array(months)].map((_, i) => {
    //             const date = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    //             return date.toLocaleString('default', { month: 'short' });
    //         });
    //     }
    // };

    const getChartLabels = (dataPoints, timeRange) => {
        const now = new Date();

        // ✅ Handle single-point case (custom range)
        if (dataPoints.length === 1) {
            return ["Selected Range", ""]; // <- give a dummy second label
        }

        if (timeRange <= 30) {
            return [...Array(timeRange)].map((_, i) => {
                const date = new Date();
                date.setDate(now.getDate() - (timeRange - 1 - i));
                return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
            });
        } else {
            const months = timeRange === 180 ? 6 : 12;
            return [...Array(months)].map((_, i) => {
                const date = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
                return date.toLocaleString("default", { month: "short" });
            });
        }
    };




    const chartData = {
        labels: getChartLabels(dataPoints, timeRange),
        datasets: [
            {
                data: dataPoints.length === 1 ? [...dataPoints, dataPoints[0]] : dataPoints, // 👈 duplicate point
                borderColor: iconColor,
                backgroundColor: `${iconColor}20`,
                tension: 0.4,
                fill: true,
                pointRadius: dataPoints.length === 1 ? 6 : 4,
                pointHoverRadius: 4,
                pointBackgroundColor: iconColor,
            },
        ],
    };



    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                mode: 'index',
                intersect: false,
                backgroundColor: '#1f2937',
                titleColor: '#fff',
                bodyColor: '#f3f4f6',
                padding: 8,
                borderRadius: 6,
            },
        },
        scales: {
            x: { display: false, grid: { display: false } },
            y: { display: false, grid: { display: false } },
        },
        elements: { line: { borderWidth: 2 } },
    };

    return (
        <div className="bg-white rounded-xl shadow p-4 flex flex-col justify-between">
            <div className="flex justify-between items-center mb-3">
                <div>
                    <p className="text-sm text-gray-500">{label}</p>
                    <h3 className="text-2xl font-bold">
                        {isCurrency ? '₹' : ''}
                        {total.toLocaleString('en-IN')}
                    </h3>
                    <p className={`text-sm ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                        {isPositive ? '▲' : '▼'} {Math.abs(percent)}% from previous
                    </p>
                </div>
                <div className={`p-3 rounded-full`} style={{ backgroundColor: bgColor }}>
                    {React.cloneElement(icon, { size: 24, color: iconColor })}
                </div>
            </div>
            <div className="relative w-full h-[80px] -mb-2 -mx-2">
                <Line data={chartData} options={chartOptions} />
            </div>
        </div>
    );
};

const getComparison = (current = [], previous = []) => {
    const currentSum = current.reduce((a, b) => a + b, 0);
    const previousSum = previous.reduce((a, b) => a + b, 0);
    const diff = currentSum - previousSum;
    const percent = previousSum === 0 ? 100 : (diff / previousSum) * 100;
    return { percent: percent.toFixed(1), isPositive: diff >= 0, currentSum, previousSum };
};

const OrderCharts = () => {
    const [rangeType, setRangeType] = useState("180");
    const [customRange, setCustomRange] = useState({ from: "", to: "" });
    const [orders, setOrders] = useState([]);
    const [users, setUsers] = useState([]);

    const [revenueData, setRevenueData] = useState([]);
    const [prevRevenueData, setPrevRevenueData] = useState([]);

    const [monthOrderData, setMonthOrderData] = useState([]);
    const [prevMonthOrderData, setPrevMonthOrderData] = useState([]);

    const [signupData, setSignupData] = useState([]);
    const [prevSignupData, setPrevSignupData] = useState([]);

    const [timeRange, setTimeRange] = useState(180); // 7, 30, 180, 365
    const url = process.env.REACT_APP_LINK;
    const userData = getDecryptedUserFromCookie("gta_mainstateuser");
    const { isSidebarOpen } = useSidebar();

    const [topProducts, setTopProducts] = useState([]);
    const [sortType, setSortType] = useState("revenue");
    const [topCategories, setTopCategories] = useState([]);

    const [statusCounts, setStatusCounts] = useState({});

    const token = decryptToken("accessToken") || ""



    const getLastXDaysData = (list, range, type = 'orders', offset = 0) => {
        const now = new Date();

        if (range <= 30) {
            const result = Array(range).fill(0);
            list.forEach(item => {
                const created = new Date(item.createdAt);
                const diff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
                if (diff >= offset && diff < offset + range) {
                    const i = range - 1 - (diff - offset);
                    if (type === 'orders' || type === 'signup') {
                        result[i] += 1;
                    } else if (type === 'revenue' && item.orderStatus === 'Delivered') {
                        const amount = parseFloat(item.amountafterdiscount || item.totalAmount || 0);
                        console.log(item.orderStatus, item.amountafterdiscount);
                        result[i] += amount;
                    }
                }
            });
            return result;
        }

        const months = range === 180 ? 6 : 12;
        const result = Array(months).fill(0);

        list.forEach(item => {
            const created = new Date(item.createdAt);
            const monthDiff =
                (now.getFullYear() - created.getFullYear()) * 12 +
                (now.getMonth() - created.getMonth());

            if (monthDiff >= offset && monthDiff < offset + months) {
                const i = months - 1 - (monthDiff - offset);
                if (type === 'orders' || type === 'signup') {
                    result[i] += 1;
                } else if (type === 'revenue' && item.orderStatus === 'Delivered') {
                    const amount = parseFloat(item.amountafterdiscount || item.totalAmount || 0);
                    console.log(item.orderStatus, item.amountafterdiscount);
                    result[i] += amount;
                }
            }
        });

        return result;
    };



    const fetchOrders = async (isCustom = false) => {
        try {
            const res = await fetch(`${url}/payment/adminorder/${userData.main_id}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const data = await res.json();

            const parsed = data.map(order => ({
                ...order,
                cart_products: typeof order.cart_products === 'string'
                    ? JSON.parse(order.cart_products)
                    : order.cart_products,
            }));

            setOrders(parsed);
            let filteredOrders = parsed;

            // ✅ If custom range active, filter by date
            if (isCustom && customRange.from && customRange.to) {
                const from = new Date(customRange.from);
                const to = new Date(customRange.to);
                filteredOrders = parsed.filter(order => {
                    const created = new Date(order.createdAt);
                    return created >= from && created <= to;
                });
            }
            const statusMap = {};
            parsed.forEach((order) => {
                const key = order.orderStatus?.toLowerCase();
                if (key) {
                    statusMap[key] = (statusMap[key] || 0) + 1;
                }
            });
            setStatusCounts(statusMap);

            if (isCustom && customRange.from && customRange.to) {
                const from = new Date(customRange.from);
                const to = new Date(customRange.to);
                const days = Math.max(1, Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1);

                // Prepare an array for each day in the range
                const dayWiseRevenue = Array(days).fill(0);
                const dayWiseOrders = Array(days).fill(0);

                filteredOrders.forEach(order => {
                    const created = new Date(order.createdAt);
                    if (created >= from && created <= to) {
                        const dayIndex = Math.floor((created - from) / (1000 * 60 * 60 * 24));
                        if (order.orderStatus === "Delivered") {
                            const amount = parseFloat(order.amountafterdiscount || order.totalAmount || 0);
                            dayWiseRevenue[dayIndex] += amount;
                        }
                        dayWiseOrders[dayIndex] += 1;
                    }
                });

                setRevenueData(dayWiseRevenue);
                setPrevRevenueData(Array(days).fill(0)); // no comparison for custom
                setMonthOrderData(dayWiseOrders);
                setPrevMonthOrderData(Array(days).fill(0));
            }else {
                setRevenueData(getLastXDaysData(filteredOrders, timeRange, 'revenue', 0));
                setPrevRevenueData(getLastXDaysData(filteredOrders, timeRange, 'revenue', timeRange));
                setMonthOrderData(getLastXDaysData(filteredOrders, timeRange, 'orders', 0));
                setPrevMonthOrderData(getLastXDaysData(filteredOrders, timeRange, 'orders', timeRange));
            }

            getTopProductsData();
        } catch (err) {
            console.error("Order fetch failed:", err);
        }
    };

    const fetchSignups = async (isCustom = false) => {
        try {
            const res = await fetch(`${url}/api/customer/all/${userData.main_id}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const userList = await res.json();
            setUsers(userList);
            let filteredUsers = userList;

            if (isCustom && customRange.from && customRange.to) {
                const from = new Date(customRange.from);
                const to = new Date(customRange.to);
                filteredUsers = userList.filter(user => {
                    const created = new Date(user.createdAt);
                    return created >= from && created <= to;
                });
            }
            if (isCustom && customRange.from && customRange.to) {
                setSignupData([filteredUsers.length]);
                setPrevSignupData([0]);
            } else {
                setSignupData(getLastXDaysData(filteredUsers, timeRange, 'signup', 0));
                setPrevSignupData(getLastXDaysData(filteredUsers, timeRange, 'signup', timeRange));
            }


        } catch (err) {
            console.error("Signup fetch failed:", err);
        }
    };

    const getTopProductsData = () => {
        const productMap = {};

        const now = new Date();
        orders.forEach(order => {
            if (order.orderStatus !== "Delivered") return;

            const created = new Date(order.createdAt);
            const diffDays = (now - created) / (1000 * 60 * 60 * 24);
            if (timeRange <= 30 && diffDays > timeRange) return;

            const monthDiff = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
            if (timeRange > 30 && monthDiff > (timeRange === 180 ? 6 : 12)) return;

            order.cart_products.forEach(product => {
                const id = product.product_id;
                const price = parseFloat(product.product_price || 0);
                const quantity = parseInt(product.quantity || 1);

                if (!productMap[id]) {
                    productMap[id] = {
                        id,
                        name: product.product_name,
                        image: product.product_image?.startsWith('/uploads')
                            ? `${url}${product.product_image}`
                            : product.product_image || "/placeholder.png",
                        price,
                        quantitySold: 0,
                        revenue: 0,
                        category: product.category || "Uncategorized",
                    };

                }

                productMap[id].quantitySold += quantity;
                productMap[id].revenue += quantity * price;
            });
        });

        const sorted = Object.values(productMap).sort((a, b) =>
            sortType === "revenue"
                ? b.revenue - a.revenue
                : b.quantitySold - a.quantitySold
        );

        setTopProducts(sorted);
        const categoryMap = {};

        Object.values(productMap).forEach((product) => {
            const category = product.category || "Uncategorized"; // Get from product data if available
            if (!categoryMap[category]) {
                categoryMap[category] = {
                    name: category,
                    count: 0,
                    revenue: 0,
                    trend: [],
                };
            }
            categoryMap[category].count += 1;
            categoryMap[category].revenue += product.revenue;
        });

        // Optional: Add mock trend data (could be derived from daily category revenue)
        Object.values(categoryMap).forEach(cat => {
            cat.trend = Array(7).fill(0).map(() => Math.floor(Math.random() * 100)); // for sparkline
        });

        const sortedCategories = Object.values(categoryMap).sort((a, b) => b.revenue - a.revenue);
        setTopCategories(sortedCategories);

    };
    useEffect(() => {
        getTopProductsData();
    }, [sortType, timeRange, orders]);

    useEffect(() => {
        const savedRange = localStorage.getItem("orderTimeRange");
        if (savedRange && savedRange !== "custom") {
            setRangeType(savedRange);
            setTimeRange(Number(savedRange));
        }
    }, []);

    const handleRangeChange = (value) => {
        setRangeType(value);

        if (value === "custom") {
            // keep previous range, but mark as custom
            return;
        }


        setTimeRange(Number(value));
        localStorage.setItem("orderTimeRange", value);
    };

    // ✅ Apply custom date range logic (no localStorage)
    const handleCustomDateChange = (key, value) => {
        setCustomRange(prev => ({ ...prev, [key]: value }));
    };

    // Example for fetching when custom dates are used
    useEffect(() => {
        if (rangeType === "custom" && customRange.from && customRange.to) {
            fetchOrders(true);
            fetchSignups(true);
        }
    }, [customRange]);



    useEffect(() => {
        if (userData?.main_id) {
            fetchOrders();
            fetchSignups();
        }
    }, [timeRange]);


    const totalOrders = Object.values(statusCounts).reduce((a, b) => a + b, 0);


    return (
        // <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="min-h-screen my-12">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-[18px] font-semibold text-gray-800">Orders Stats</h2>
                <select
                    className="border px-2 py-1 rounded text-sm"
                    value={rangeType}
                    onChange={(e) => handleRangeChange(e.target.value)}
                >
                    <option value="7">Last 7 Days</option>
                    <option value="30">Last 30 Days</option>
                    <option value="180">Last 6 Months</option>
                    <option value="365">Last 1 Year</option>
                    <option value="custom">Custom Date</option>
                </select>
                {rangeType === "custom" && (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={customRange.from}
                            onChange={(e) => handleCustomDateChange("from", e.target.value)}
                            className="border px-2 py-1 rounded text-sm"
                        />
                        <span>to</span>
                        <input
                            type="date"
                            value={customRange.to}
                            onChange={(e) => handleCustomDateChange("to", e.target.value)}
                            className="border px-2 py-1 rounded text-sm"
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatChartCard
                    label="Orders"
                    dataPoints={monthOrderData}
                    comparison={getComparison(monthOrderData, prevMonthOrderData)}
                    icon={<Package />}
                    iconColor="#10b981"
                    bgColor="#d1fae5"
                    timeRange={timeRange}
                />

                <StatChartCard
                    label="Revenue Generated"
                    dataPoints={revenueData}
                    comparison={getComparison(revenueData, prevRevenueData)}
                    icon={<IndianRupee />}
                    iconColor="#f59e0b"
                    bgColor="#fef3c7"
                    timeRange={timeRange}
                    isCurrency={true}
                />

                <StatChartCard
                    label="Customers Signed In"
                    dataPoints={signupData}
                    comparison={getComparison(signupData, prevSignupData)}
                    icon={<Users />}
                    iconColor="#3b82f6"
                    bgColor="#dbeafe"
                    timeRange={timeRange}
                />
            </div>
            <div className="my-12 rounded-lg shadow-md p-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-[18px] font-semibold text-gray-800">Top Products & Categories</h3>
                    <div className="space-x-2 flex">
                        <Ripples>
                            <button
                                className={`px-3 py-1 rounded ${sortType === 'revenue' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                onClick={() => setSortType('revenue')}
                            >
                                Revenue
                            </button>
                        </Ripples>

                        <Ripples>
                            <button
                                className={`px-3 py-1 rounded ${sortType === 'quantity' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                onClick={() => setSortType('quantity')}
                            >
                                Quantity
                            </button>
                        </Ripples>
                    </div>
                </div>

                {/* Flexbox layout for Products and Categories */}
                <div className="flex flex-col lg:flex-row gap-4 ">
                    {/* Products (flex-2) */}
                    <div className="flex-2 w-full">
                        <ul className="space-y-3">
                            {topProducts.slice(0, 5).map((product, index) => (
                                <li
                                    key={product.id}
                                    className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3 hover:bg-gray-50 transition"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                        <img
                                            src={product.image || "/placeholder.png"}
                                            alt={product.name}
                                            className="w-10 h-10 rounded object-cover border"
                                        />
                                        <div>
                                            <h4 className="font-semibold text-sm text-gray-800">{product.name}</h4>
                                            <p className="text-xs text-gray-500">Price: ₹{product.price.toLocaleString()}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm font-medium text-right text-blue-600">
                                        {sortType === "revenue"
                                            ? `₹${product.revenue.toLocaleString()}`
                                            : `${product.quantitySold} units`}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Categories (flex-1) */}
                    <div className="flex-1 w-full min-w-72">
                        {/* <h3 className="text-lg font-semibold mb-2">Top Categories</h3> */}
                        <ul className="space-y-3">
                            {topCategories.slice(0, 5).map((category, index) => {
                                const sparklineData = {
                                    labels: category.trend.map((_, i) => `Day ${i + 1}`),
                                    datasets: [{
                                        data: category.trend,
                                        borderColor: "#3b82f6",
                                        backgroundColor: "#dbeafe",
                                        fill: true,
                                        tension: 0.4,
                                        pointRadius: 0,
                                    }]
                                };

                                const sparklineOptions = {
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    elements: { line: { borderWidth: 2 }, point: { radius: 0 } },
                                    plugins: { legend: { display: false }, tooltip: { enabled: false } },
                                    scales: {
                                        x: { display: false },
                                        y: { display: false },
                                    },
                                };

                                return (
                                    <li
                                        key={category.name}
                                        className="bg-white rounded-lg shadow px-4 py-3 hover:bg-gray-50 transition"
                                    >
                                        <div className="flex justify-between items-center mb-1">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800">#{index + 1} {category.name}</p>
                                                <p className="text-xs text-gray-500">{category.count} products</p>
                                            </div>
                                            <p className="text-sm font-medium text-blue-600">
                                                ₹{category.revenue.toLocaleString()}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
            {/* Order Status Progress Bar */}
            <div className="my-12 rounded-lg shadow-md p-4">
                <h3 className="text-[18px] font-semibold text-gray-800 mb-2">Order Status Summary</h3>

                {/* Horizontal progress bar */}
                <div className="w-full flex rounded overflow-hidden h-4 mb-3">
                    {statusList.map((status) => {
                        const count = statusCounts[status.key] || 0;
                        const percent = totalOrders > 0 ? (count / totalOrders) * 100 : 0;
                        return (
                            <div
                                key={status.key}
                                className="h-full"
                                style={{
                                    width: `${percent}%`,
                                    backgroundColor: status.color,
                                    transition: "width 0.3s ease",
                                }}
                            />
                        );
                    })}
                </div>

                {/* Status legends */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm text-gray-700">
                    {statusList.map((status) => (
                        <div key={status.key} className="flex items-center space-x-2">
                            <span
                                className="inline-block w-3 h-3 rounded-full"
                                style={{ backgroundColor: status.color }}
                            ></span>
                            <span>{status.label} ({statusCounts[status.key] || 0})</span>
                        </div>
                    ))}
                </div>
            </div>

        </div>
        // </div>
    );
};

export default OrderCharts;
